import React, { useContext, useState } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import TripGroupSelector from '../TripGroupSelector';
import '../../../styles/modules/waiver-reporting.less';
import DateSelector from '../../lib/DateSelector/DateSelector';
import DateRangePresets from '../DateRangePresets';
import Accordion from '../../components/accordion/Accordion';
import { PageContext } from '../../pages/components/page';

interface IssuesReportingFormProps {
  onReset: () => void;
  fetchReport: (searchRequest: any) => void;
  searching: boolean;
}

const IssuesReportingForm: React.FC<IssuesReportingFormProps> = ({ onReset, fetchReport, searching }) => {
  const { handleSubmit, formState: { isValid }, reset } = useForm({ mode: 'onChange' });
  const [startTime, setStartTime] = useState<Date>(moment().subtract(1, 'months').startOf('month').toDate());
  const [endTime, setEndTime] = useState<Date>(moment().subtract(1, 'months').endOf('month').toDate());
  const [dateRangePreset, setDateRangePreset] = useState<string>('lastmonth');
  const [tripGroupIds, setTripGroupIds] = useState<string[]>([]);
  const [accordionOpen, setAccordionOpen] = useState<boolean>(true);
  const [formKey, setFormKey] = useState<number>(Date.now());
  const { pushAlert }: { pushAlert: (type: string, message: string) => void } = useContext(PageContext);
  const [resetTripGroups, setResetTripGroups] = useState<boolean>(false);



  const validate = (): string | undefined => {
    if (!isValid) {
      return 'Reporting criteria is invalid';
    }
    if (!startTime || !endTime) {
      return 'Invalid date format';
    }
    const startMoment = moment(startTime);
    const endMoment = moment(endTime);
    if (!startMoment.isValid() || !endMoment.isValid()) {
      return 'Invalid date format';
    }
    if (endMoment.isBefore(startMoment)) {
      return 'End time must be after start time';
    }
    if (endMoment.diff(startMoment, 'years', true) > 1) {
      return 'Maximum one year range supported';
    }
    return;
  };

  const buildSearchRequest = () => {
    return {
      tripGroupIds,
      resolveByMin: moment(startTime).format('YYYY-MM-DDTHH:mm:ss'),
      resolveByMax: moment(endTime).format('YYYY-MM-DDTHH:mm:ss'),
      clientOffset: moment().format('Z'),
    };
  };

  const onSubmitForm = async () => {
    const error = validate();
    if (error) {
      pushAlert('danger', error);
      return;
    }
    if (searching) {
      return;
    }
    const searchRequest = buildSearchRequest();
    console.log('searchRequest:', searchRequest);
    fetchReport(searchRequest);
    setAccordionOpen(false);
  };

  const onResetForm = () => {
    setDateRangePreset('lastmonth');
    setStartTime(moment().subtract(1, 'months').startOf('month').toDate());
    setEndTime(moment().subtract(1, 'months').endOf('month').toDate());
    setResetTripGroups(true);
    reset();
    onReset();
    setFormKey(Date.now());
    setAccordionOpen(true);
  };

  return (
    <div className="row search col-md-12">
      <div className="search-form">
        <Accordion
          id="issues-reporting-accordion"
          className='uib-accordion'
          panels={[
            {
              id: 'reporting-criteria',
              title: 'REPORTING CRITERIA',
              show: accordionOpen,
              setToggleState: setAccordionOpen,
              children: (
                <form
                  name="searchform"
                  role="form"
                  noValidate
                  onSubmit={handleSubmit(onSubmitForm)}
                >
                  <div className="row">
                    <TripGroupSelector
                      key="trip-group-selector"
                      className="col-sm-12 col-md-3 col-lg-3"
                      onChange={setTripGroupIds}
                      resetTripGroups={resetTripGroups}
                      setResetTripGroups={setResetTripGroups}
                    />
                    <div className="col-sm-12 col-md-9 col-lg-9">
                      <div className="row">
                        <div className={'form-group row col-xs-12 col-sm-12 col-md-6 resolve-by resolve-by-date-selectors'}>
                          <DateSelector
                            key={`start-${formKey}`}
                            label="Start Date:"
                            includeTime={true}
                            value={startTime.toString()}
                            dateFormat="dd-MMM-yyyy"
                            showMonthYearPicker={false}
                            onChange={(date) => {
                              if (date) {
                                setAccordionOpen(true);
                                setStartTime(date);
                              }
                            }}
                            placeholderText="DD-MM-YYYY"
                            initialTime="12:00 AM"
                          />
                        </div>
                        <div className={'form-group row col-xs-12 col-sm-12 col-md-6 resolve-by resolve-by-date-selectors'}>
                          <DateSelector
                            key={`end-${formKey}`}
                            label="End Date:"
                            includeTime={true}
                            value={endTime.toString()}
                            dateFormat="dd-MMM-yyyy"
                            showMonthYearPicker={false}
                            onChange={(date) => {
                              if (date) {
                                setAccordionOpen(true);
                                setEndTime(date);
                              }
                            }}
                            placeholderText="DD-MM-YYYY"
                            initialTime="11:59 PM"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-sm-12 col-md-3 col-lg-3"></div>
                      <DateRangePresets
                        className="col-sm-12 col-md-9 col-lg-9"
                        value={dateRangePreset}
                        onChange={(startDate, endDate, preset) => {
                          setAccordionOpen(true);
                          setStartTime(startDate);
                          setEndTime(endDate);
                          setDateRangePreset(preset);
                          setFormKey(Date.now());
                        }}
                      />
                    </div>
                  </div>
                  <div className="row form-group-sep">
                    <div className="col-md-8"></div>
                    <div className="col-md-4 search-button">
                      <div className="form-group">
                        <button
                          type="button"
                          className="btn btn-default button-clear"
                          onClick={onResetForm}
                        >
                          CLEAR
                        </button>
                        <button
                          type="submit"
                          className="btn btn-default button-action"
                        >
                          REPORT
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default IssuesReportingForm;